import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CalendarClock, LayoutGrid, Telescope } from 'lucide-react';
import { appWindowName, openAppTab } from '../lib/appSwitcher.js';

const APP_ICONS = { synolog: CalendarClock, synosight: Telescope };
const PANEL_WIDTH = 220;

/**
 * Menu « Applications » de l'en-tête (synotis-support#23) : liste les apps
 * déjà filtrées (`apps`, cf. `lib/appSwitcher.js`), l'app courante marquée
 * « actuelle ». Chaque entrée est un vrai lien nommé (clic milieu / Cmd+clic
 * conservés) ; le clic simple passe par `openAppTab` pour réutiliser l'onglet
 * sans le recharger. Fermeture au clic extérieur, à Échap et après un choix.
 */
export default function AppSwitcherMenu({ apps, buttonStyle, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    const place = () => {
      const r = buttonRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8)) });
    };
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target) || buttonRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        buttonRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const onAppClick = (e, app) => {
    // Clic milieu / modifié : comportement natif du lien (nouvel onglet).
    if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    // Popup refusée : on laisse le lien nommé faire l'ouverture.
    if (openAppTab(app)) e.preventDefault();
    setOpen(false);
  };

  const itemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '0.55rem',
    width: '100%',
    padding: '0.45rem 0.6rem',
    borderRadius: 'var(--radius-sm)',
    fontSize: '0.88rem',
    textDecoration: 'none',
    boxSizing: 'border-box',
  };

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Applications"
        aria-label="Applications"
        aria-haspopup="menu"
        aria-expanded={open}
        style={buttonStyle}
      >
        <LayoutGrid size={18} />
      </button>

      {open && (
        <div
          ref={panelRef}
          role="menu"
          aria-label="Applications"
          style={{
            position: 'fixed',
            top: pos.top,
            left: pos.left,
            width: PANEL_WIDTH,
            zIndex: 200,
            background: 'var(--card-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            padding: '0.4rem',
          }}
        >
          <div style={{ padding: '0.35rem 0.6rem 0.25rem', fontSize: '0.7rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em', color: 'var(--text-muted)' }}>
            Applications
          </div>
          {apps.map((app) => {
            const Icon = APP_ICONS[app.key] || LayoutGrid;
            if (app.current) {
              return (
                <div
                  key={app.key}
                  role="menuitem"
                  aria-current="page"
                  aria-disabled="true"
                  style={{ ...itemStyle, color: 'var(--text)', fontWeight: 600, cursor: 'default' }}
                >
                  <Icon size={15} style={{ flexShrink: 0 }} />
                  <span style={{ flex: 1 }}>{app.label}</span>
                  <span
                    style={{
                      padding: '0.1rem 0.5rem',
                      borderRadius: 999,
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      background: 'var(--accent)',
                      color: '#fff',
                    }}
                  >
                    actuelle
                  </span>
                </div>
              );
            }
            return (
              <a
                key={app.key}
                role="menuitem"
                href={app.url}
                target={appWindowName(app)}
                title={`Ouvrir ${app.label}`}
                onClick={(e) => onAppClick(e, app)}
                style={{ ...itemStyle, color: 'var(--text)', cursor: 'pointer' }}
              >
                <Icon size={15} style={{ flexShrink: 0 }} />
                <span style={{ flex: 1 }}>{app.label}</span>
              </a>
            );
          })}
        </div>
      )}
    </>
  );
}
